import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { SPONSA_CSS } from "./sponsaCSS";
import apisecured from "../../../api/apisecured.js";
import { setNotificationPreferences } from "../../../State/authSlicer.js";
import { useUnsavedChanges } from "../../../hooks/useUnsavedChanges.js";
import { setToast } from "../../../State/toastSlice.js";

function SaveBar({ visible, onDiscard, onSave, saving }) {
  return (
    <div className={`save-bar${visible ? " visible" : ""}`}>
      <div className="save-bar-inner">
        <span className="save-bar-text">You have unsaved changes</span>
        <button
          className="btn btn-secondary"
          onClick={onDiscard}
          disabled={saving}
        >
          Discard
        </button>
        <button className="btn btn-primary" onClick={onSave} disabled={saving}>
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, disabled }) {
  return (
    <label className="toggle-switch">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <span className="toggle-slider" />
    </label>
  );
}

export default function NotificationsSettingPage() {
  const dispatch = useDispatch();
  const ReceiveAllTips = useSelector((state) => state.auth.ReceiveAllTips);
  const ReceiveGoalFunded = useSelector(
    (state) => state.auth.ReceiveGoalFunded,
  );

  // Local state for pending changes
  const [localTips, setLocalTips] = useState(null);
  const [localGoal, setLocalGoal] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useUnsavedChanges(dirty);

  // Fetch preferences from API if not already in Redux
  useEffect(() => {
    if (ReceiveAllTips === null || ReceiveGoalFunded === null) {
      const fetchNotificationPreferences = async () => {
        try {
          const response = await apisecured.get(
            "account/get-notification-preferences",
          );
          dispatch(
            setNotificationPreferences({
              ReceiveAllTips: response.data.receiveAllTips,
              ReceiveGoalFunded: response.data.receiveGoalFunded,
            }),
          );
        } catch (error) {
          console.error("Error fetching notification preferences:", error);
          dispatch(
            setToast({
              message: "Failed to load notification settings.",
              type: "error",
            }),
          );
        }
      };

      fetchNotificationPreferences();
    }
  }, [ReceiveAllTips, ReceiveGoalFunded, dispatch]);

  // Sync local state from Redux once loaded
  useEffect(() => {
    if (ReceiveAllTips !== null && localTips === null) {
      setLocalTips(!!ReceiveAllTips);
    }
  }, [ReceiveAllTips]);

  useEffect(() => {
    if (ReceiveGoalFunded !== null && localGoal === null) {
      setLocalGoal(!!ReceiveGoalFunded);
    }
  }, [ReceiveGoalFunded]);

  const handleTipsToggle = () => {
    setLocalTips((prev) => !prev);
    setDirty(true);
  };

  const handleGoalToggle = () => {
    setLocalGoal((prev) => !prev);
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apisecured.post("account/update-notification-preference", {
        ReceiveAllTips: localTips,
        ReceiveGoalFunded: localGoal,
      });

      // Persist to Redux only on success
      dispatch(
        setNotificationPreferences({
          ReceiveAllTips: localTips,
          ReceiveGoalFunded: localGoal,
        }),
      );

      setDirty(false);
      dispatch(
        setToast({
          message: "Notification settings updated.",
          type: "success",
        }),
      );
    } catch (error) {
      console.error("Update error:", error);
      dispatch(
        setToast({
          message: "Failed to update notification settings.",
          type: "error",
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setLocalTips(!!ReceiveAllTips);
    setLocalGoal(!!ReceiveGoalFunded);
    setDirty(false);
  };

  return (
    <>
      <style>{SPONSA_CSS}</style>
      <div className="sp-settings-wrap">
        <div className="sp-settings-inner">
          <header className="page-header">
            <h1 className="page-title">Notification Settings</h1>
            <p className="page-description">
              Choose how and when you want to be notified.
            </p>
          </header>

          {/* On-site */}
          <section className="settings-section">
            <div className="section-header">
              <div className="section-icon purple">
                <svg
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
              </div>
              <div className="section-title-group">
                <h2 className="section-title">On-Site Notifications</h2>
                <p className="section-subtitle">
                  Notifications shown within SPONSA
                </p>
              </div>
            </div>

            <div className="toggle-row">
              <div className="toggle-info">
                <h4>Tip Received</h4>
                <p>Notify when you receive a tip</p>
              </div>
              <Toggle
                checked={localTips ?? false}
                onChange={handleTipsToggle}
              />
            </div>

            <div className="toggle-row">
              <div className="toggle-info">
                <h4>Goal Completed</h4>
                <p>Notify when a goal reaches 100%</p>
              </div>
              <Toggle
                checked={localGoal ?? false}
                onChange={handleGoalToggle}
              />
            </div>
          </section>

          {/* Email — coming soon */}
          <section className="settings-section">
            <div className="section-header">
              <div className="section-icon coral">
                <svg
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <div className="section-title-group">
                <h2 className="section-title">
                  Email Notifications{" "}
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: "var(--yellow)",
                      background: "var(--yellow-dim)",
                      padding: "3px 8px",
                      borderRadius: 6,
                      marginLeft: 8,
                      verticalAlign: "middle",
                    }}
                  >
                    Coming Soon
                  </span>
                </h2>
                <p className="section-subtitle">
                  Notifications sent to your email
                </p>
              </div>
            </div>

            <div style={{ opacity: 0.45, pointerEvents: "none" }}>
              {[
                {
                  title: "Tip Received",
                  desc: "Get an email when you receive a tip",
                },
                {
                  title: "Goal Milestones",
                  desc: "Get an email at 25%, 50%, 75% progress",
                },
                {
                  title: "Goal Completed",
                  desc: "Get an email when a goal reaches 100%",
                },
                {
                  title: "Referral Code Redeemed",
                  desc: "When someone signs up using your referral code",
                },
                {
                  title: "Weekly Summary",
                  desc: "Receive a weekly digest of your earnings",
                },
                {
                  title: "Product Updates",
                  desc: "New features and platform announcements",
                },
              ].map(({ title, desc }) => (
                <div className="toggle-row" key={title}>
                  <div className="toggle-info">
                    <h4>{title}</h4>
                    <p>{desc}</p>
                  </div>
                  <Toggle checked={false} onChange={() => {}} disabled />
                </div>
              ))}
            </div>
          </section>

          <div style={{ height: 80 }} />
        </div>
      </div>

      <SaveBar
        visible={dirty}
        onDiscard={handleDiscard}
        onSave={handleSave}
        saving={saving}
      />
    </>
  );
}
